import { Mark, mergeAttributes, Node } from '@tiptap/core';

export const SvgNode = Node.create({
  name: 'svgNode',
  group: 'inline',
  atom: true,
  inline: true,

  parseHTML() {
    return [
      {
        tag: 'span[data-svg-node]',
      },
    ];
  },

  addAttributes() {
    return {
      svg: {
        default: null,
        parseHTML: element => {
          return element.innerHTML;
        },
        renderHTML: attributes => {
          if (!attributes.svg) {
            return {};
          }

          return {
            'data-svg': attributes.svg,
          };
        },
      },
    };
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        'data-svg-node': '',
      }),
    ];
  },

  addNodeView() {
    return ({ node }) => {
      const wrapper = document.createElement('div');
      wrapper.classList.add('svg-node');
      wrapper.innerHTML = node.attrs.svg;
      return {
        dom: wrapper,
      };
    };
  },
});

export const SlashCommandGroupTitleMark = Mark.create({
  name: 'slashCommandGroupTitle',
  group: 'inline',

  parseHTML() {
    return [
      {
        tag: 'span[data-type="slash-command-group-title"]',
      },
    ];
  },

  addOptions() {
    return {
      type: 'slashCommandGroupTitle',
    };
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      {
        ...HTMLAttributes,
        'data-type': 'slash-command-group-title',
      },
      0,
    ];
  },
});

export const CustomNode = Node.create({
  name: 'custom',
  group: 'inline',
  inline: true,
  selectable: true,
  atom: true,
  // priority: 102,

  content: 'inline*',

  marks: 'slashCommandGroupTitle',

  parseHTML() {
    return [
      {
        tag: 'span[data-type="slash-command"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      {
        ...HTMLAttributes,
        'data-type': 'slash-command',
        contenteditable: 'false',
      },
      0,
    ];
  },
});

const parseJson = <T>(raw: string | null, fallback: T): T => {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
};

const toNumberOrNull = (value: string | null) => {
  if (value === null || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

/**
 * A Prompt Template in the composer: one atom that shows the title and keeps
 * the body, template id, version and filled values for the composed message.
 * Switch-off strings store the string as title and body, with no template id.
 */
export const PromptChipNode = Node.create({
  name: 'promptChip',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      templateId: {
        default: null,
        parseHTML: element =>
          toNumberOrNull(element.getAttribute('data-template-id')),
        renderHTML: attributes =>
          attributes.templateId == null
            ? {}
            : { 'data-template-id': String(attributes.templateId) },
      },
      version: {
        default: null,
        parseHTML: element =>
          toNumberOrNull(element.getAttribute('data-version')),
        renderHTML: attributes =>
          attributes.version == null
            ? {}
            : { 'data-version': String(attributes.version) },
      },
      title: {
        default: '',
        parseHTML: element =>
          element.getAttribute('data-title') ?? element.textContent ?? '',
        renderHTML: attributes => ({ 'data-title': attributes.title }),
      },
      body: {
        default: '',
        parseHTML: element =>
          element.getAttribute('data-body') ?? element.textContent ?? '',
        renderHTML: attributes => ({ 'data-body': attributes.body }),
      },
      values: {
        default: {},
        parseHTML: element =>
          parseJson(element.getAttribute('data-values'), {}),
        renderHTML: attributes => ({
          'data-values': JSON.stringify(attributes.values ?? {}),
        }),
      },
      // The template's text with its `{name}` slots and its variables, so a
      // click on the chip reopens the fill step (VH3_CONNECT #592).
      templateBody: {
        default: '',
        parseHTML: element => element.getAttribute('data-template-body') ?? '',
        renderHTML: attributes =>
          attributes.templateBody
            ? { 'data-template-body': attributes.templateBody }
            : {},
      },
      variables: {
        default: [],
        parseHTML: element =>
          parseJson(element.getAttribute('data-variables'), []),
        renderHTML: attributes =>
          attributes.variables?.length
            ? { 'data-variables': JSON.stringify(attributes.variables) }
            : {},
      },
      /** True when the wording was changed for this message only. */
      edited: {
        default: false,
        parseHTML: element => element.getAttribute('data-edited') === 'true',
        renderHTML: attributes =>
          attributes.edited ? { 'data-edited': 'true' } : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-type="prompt-chip"]' }];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'prompt-chip',
        class: 'prompt-chip',
        contenteditable: 'false',
      }),
      node.attrs.title,
    ];
  },

  renderText({ node }) {
    return node.attrs.body;
  },
});

export const slashExtensionNodes = [
  SvgNode,
  SlashCommandGroupTitleMark,
  CustomNode,
  PromptChipNode,
];
